import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { useSEO } from "@/lib/useSEO";

export default function Login() {
  useSEO({ title: "Sign In — The Flow State Method", description: "Sign in to your Flow State Method account to view sessions, pay, and manage bookings.", path: "/login" });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get("next") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || ""}.`);
      // Prefer ?next=, then location.state.from, then default for the user's role
      const dest = nextUrl || location.state?.from || (u.role === "admin" ? "/admin" : "/portal");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#050505]" data-testid="login-page">
      <div className="theme-on-dark hidden md:block relative">
        <img src="https://images.pexels.com/photos/5750963/pexels-photo-5750963.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#050505]/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" data-testid="login-logo">
            <BrandMark size="md" />
          </Link>
          <div>
            <h2 className="font-display uppercase text-5xl tracking-tight leading-none">Enter<br />the <span className="text-[#FF331F]">ring</span>.</h2>
            <p className="text-zinc-400 mt-4 max-w-sm text-sm">Sign in to manage sessions, view your schedule, and stay locked in.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Sign in</div>
            <h1 className="font-display uppercase text-3xl">Welcome back.</h1>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0F0F0F] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="login-email" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Password</Label>
              <Link
                to="/forgot-password"
                className="text-[11px] uppercase tracking-widest font-bold text-[#FF331F] hover:underline"
                data-testid="login-forgot-link"
              >Forgot?</Link>
            </div>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-[#0F0F0F] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="login-password" />
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <p className="text-sm text-zinc-500 text-center">
            No account?{" "}
            <Link
              to={nextUrl ? `/register?next=${encodeURIComponent(nextUrl)}` : "/register"}
              className="text-[#FF331F] hover:underline"
              data-testid="login-register-link"
            >Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
